/**
 * Step 1 — Seleziona azienda esistente OPPURE crea nuova azienda.
 * Layout e campi identici a StepAziendaData dello store.
 */
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Building2, ChevronDown, Plus } from 'lucide-react';

export default function StepAziendaMSSP({
  aziende,
  selectedAziendaId,
  onSelectAzienda,
  newAziendaData,
  onChangeNewAzienda,
  isCreatingNew,
  onToggleCreateNew,
  settori,
  errors,
}) {
  const [showArrow, setShowArrow] = useState(false);
  const containerRef = useRef(null);

  const checkOverflow = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setShowArrow(
      el.scrollHeight > el.clientHeight + 10 &&
      el.scrollTop + el.clientHeight < el.scrollHeight - 10
    );
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    checkOverflow();
    el.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);
    return () => {
      el.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [checkOverflow, isCreatingNew]);

  const scrollDown = () => {
    const el = containerRef.current;
    if (el) el.scrollBy({ top: 200, behavior: 'smooth' });
  };

  const handleFieldChange = (field) => (e) => {
    onChangeNewAzienda({ ...newAziendaData, [field]: e.target.value });
  };

  const handleUrlBlur = () => {
    const v = (newAziendaData.url || '').trim();
    if (v && !/^https?:\/\//i.test(v)) {
      onChangeNewAzienda({ ...newAziendaData, url: `https://${v}` });
    }
  };

  const fieldError = (field) => {
    const key = `azienda.${field}`;
    const err = errors?.[key] || errors?.[field];
    if (!err) return null;
    const msg = Array.isArray(err) ? err[0] : err;
    return <p className="text-red-500 text-[11px] mt-0.5">{msg}</p>;
  };

  const ic =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none transition-shadow';

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="w-5 h-5 text-yellow-600" />
        <h2 className="text-lg font-bold text-gray-900">Dati Azienda</h2>
      </div>

      <div ref={containerRef} className="flex-1 min-h-0 overflow-y-auto pr-2">
        {/* Selezione azienda esistente o creazione */}
        <div className="mb-4">
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => onToggleCreateNew(false)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                !isCreatingNew
                  ? 'bg-yellow-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Building2 size={16} className="inline mr-2" />
              Seleziona Esistente
            </button>
            <button
              type="button"
              onClick={() => onToggleCreateNew(true)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                isCreatingNew
                  ? 'bg-yellow-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Plus size={16} className="inline mr-2" />
              Crea Nuova
            </button>
          </div>
        </div>

        {!isCreatingNew ? (
          /* ── Selezione azienda esistente ── */
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Azienda <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedAziendaId || ''}
              onChange={(e) => onSelectAzienda(e.target.value)}
              className={ic}
            >
              <option value="">— Seleziona un'azienda —</option>
              {(aziende || []).map((az) => (
                <option key={az.id_azienda} value={az.id_azienda}>
                  {az.nome_azienda}
                </option>
              ))}
            </select>
            {fieldError('id_azienda')}
          </div>
        ) : (
          /* ── Form creazione nuova azienda (stesso layout dello store) ── */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            {/* Nome azienda */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Azienda <span className="text-red-500">*</span>
              </label>
              <input type="text" value={newAziendaData.nome_azienda || ''} onChange={handleFieldChange('nome_azienda')} className={ic} />
              {fieldError('nome_azienda')}
            </div>

            {/* Partita IVA */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Partita IVA <span className="text-red-500">*</span>
              </label>
              <input type="text" value={newAziendaData.partita_iva || ''} onChange={handleFieldChange('partita_iva')} maxLength={11} className={ic} />
              {fieldError('partita_iva')}
            </div>

            {/* Codice Fiscale */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
              <input type="text" value={newAziendaData.codice_fiscale || ''} onChange={handleFieldChange('codice_fiscale')} maxLength={16} placeholder="CF 16 caratteri o P.IVA 11 cifre" className={ic} />
              {fieldError('codice_fiscale')}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input type="email" value={newAziendaData.email || ''} onChange={handleFieldChange('email')} className={ic} />
              {fieldError('email')}
            </div>

            {/* PEC */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PEC</label>
              <input type="email" value={newAziendaData.pec || ''} onChange={handleFieldChange('pec')} className={ic} />
            </div>

            {/* URL aziendale */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL aziendale</label>
              <input type="url" value={newAziendaData.url || ''} onChange={handleFieldChange('url')} onBlur={handleUrlBlur} placeholder="www.azienda.it" className={ic} />
            </div>

            {/* Telefono */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
              <input type="tel" value={newAziendaData.telefono || ''} onChange={handleFieldChange('telefono')} className={ic} />
            </div>

            {/* Indirizzo */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo</label>
              <input type="text" value={newAziendaData.indirizzo || ''} onChange={handleFieldChange('indirizzo')} className={ic} />
            </div>

            {/* CAP */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
              <input type="text" value={newAziendaData.cap || ''} onChange={handleFieldChange('cap')} maxLength={5} className={ic} />
            </div>

            {/* Città */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
              <input type="text" value={newAziendaData.citta || ''} onChange={handleFieldChange('citta')} className={ic} />
            </div>

            {/* Comune */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Comune</label>
              <input type="text" value={newAziendaData.comune || ''} onChange={handleFieldChange('comune')} className={ic} />
            </div>

            {/* Provincia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
              <input type="text" value={newAziendaData.provincia || ''} onChange={handleFieldChange('provincia')} maxLength={2} className={ic} />
            </div>

            {/* Stato */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
              <input type="text" value={newAziendaData.stato || ''} onChange={handleFieldChange('stato')} className={ic} />
            </div>

            {/* N. Dipendenti */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">N. Dipendenti</label>
              <input type="number" value={newAziendaData.numero_dipendenti || ''} onChange={handleFieldChange('numero_dipendenti')} min={1} className={ic} />
            </div>

            {/* Fatturato Annuo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fatturato Annuo (€)</label>
              <input type="number" value={newAziendaData.fatturato_annuo || ''} onChange={handleFieldChange('fatturato_annuo')} min={0} step="0.01" className={ic} />
            </div>

            {/* Settore */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Settore</label>
              <select value={newAziendaData.id_settore || ''} onChange={handleFieldChange('id_settore')} className={ic}>
                <option value="">— Seleziona —</option>
                {(settori || []).map((s) => (
                  <option key={s.id_settore} value={s.id_settore}>
                    {s.settore || s.categoria || s.descrizione}
                  </option>
                ))}
              </select>
            </div>

            {/* Codici ATECO */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Codici ATECO</label>
              <input type="text" value={newAziendaData.codice_ateco || ''} onChange={handleFieldChange('codice_ateco')} className={ic} />
            </div>
          </div>
        )}
      </div>

      {/* Scroll-down arrow */}
      {showArrow && (
        <div className="flex justify-center mt-2">
          <button
            type="button"
            onClick={scrollDown}
            title="Scorri in basso per compilare gli altri campi"
            className="group flex flex-col items-center animate-bounce text-yellow-600 hover:text-yellow-700 transition-colors focus:outline-none"
          >
            <ChevronDown className="w-7 h-7" />
            <span className="text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gray-500 mt-0.5">
              Scorri per gli altri campi
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
